import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { registerUser, loginUser, resendVerification, forgotPassword } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const formatErr = (d) => {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.message) return d.message;
  return d?.msg || String(d);
};

export const AuthModal = () => {
  const { authOpen, setAuthOpen, authInitialMode, loginWithToken } = useAuth();
  // Mode: "login" | "register" | "check-email" | "unverified" | "forgot" | "forgot-sent"
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (authOpen) setMode(authInitialMode || "login");
  }, [authOpen, authInitialMode]);

  useEffect(() => {
    if (!resendCooldown) return;
    const t = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const reset = () => { setName(""); setPassword(""); setAcceptedTerms(false); setMarketingConsent(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !acceptedTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        const res = await registerUser({
          email, password, name,
          accepted_terms: acceptedTerms,
          marketing_consent: marketingConsent,
        });
        // NEW FLOW: signup no longer returns a token — user must verify first.
        setPendingEmail(email);
        setMode("check-email");
        reset();
        toast.success(res.message || "Check your email to verify your account.");
      } else if (mode === "login") {
        const data = await loginUser({ email, password });
        loginWithToken(data.token, data.user, "signed_in");
        setAuthOpen(false);
        const m = data.migrated ? ` ${data.migrated} previous report${data.migrated > 1 ? "s" : ""} saved to your account.` : "";
        toast.success(`Welcome back${data.user?.name ? ", " + data.user.name : ""}!` + m);
        reset();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "email_not_verified") {
        setPendingEmail(detail.email || email);
        setMode("unverified");
        return;
      }
      toast.error(formatErr(detail) || "Authentication failed.");
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setPendingEmail(email);
      setMode("forgot-sent");
    } catch (err) {
      // Endpoint returns 200 for enumeration safety; only real errors here.
      toast.error(formatErr(err?.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await resendVerification(pendingEmail || email);
      toast.success("Verification email sent — check your inbox.");
      setResendCooldown(30);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Could not send email.");
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // -------------------- Rendering --------------------
  const isFormMode = mode === "login" || mode === "register";

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="rounded-none border-border sm:max-w-[420px]" data-testid="auth-modal">
        {(mode === "check-email" || mode === "unverified") && (
          <div className="text-center py-2" data-testid={`auth-${mode}`}>
            <div className="mx-auto w-14 h-14 rounded-full bg-oxford/10 flex items-center justify-center text-oxford mb-3">
              <MailCheck size={26} />
            </div>
            <DialogTitle className="font-serif text-2xl tracking-tight mb-2">
              {mode === "check-email" ? "Check your email" : "Please verify your email"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {mode === "check-email"
                ? <>We sent a verification link to <b className="text-foreground">{pendingEmail}</b>. Click it to activate your account. The link expires in 24 hours.</>
                : <>Your account exists but hasn&apos;t been verified yet. Please click the link we sent to <b className="text-foreground">{pendingEmail}</b> before logging in.</>}
            </p>
            <Button onClick={resend} disabled={loading || resendCooldown > 0} data-testid="auth-resend-verification"
              className="w-full rounded-none bg-oxford hover:bg-[#2C5282] text-white h-11 gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
            </Button>
            <button onClick={() => setMode("login")} data-testid="auth-back-to-login"
              className="mt-4 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
              <ArrowLeft size={13} /> Back to log in
            </button>
          </div>
        )}

        {mode === "forgot-sent" && (
          <div className="text-center py-2" data-testid="auth-forgot-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-oxford/10 flex items-center justify-center text-oxford mb-3"><MailCheck size={26} /></div>
            <DialogTitle className="font-serif text-2xl tracking-tight mb-2">Check your email</DialogTitle>
            <p className="text-sm text-muted-foreground mb-4">
              If an account exists for <b className="text-foreground">{pendingEmail}</b>, we&apos;ve sent a password-reset link. It expires in 1 hour.
            </p>
            <button onClick={() => setMode("login")}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
              <ArrowLeft size={13} /> Back to log in
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <div>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl tracking-tight">Reset your password</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">Enter your email and we&apos;ll send you a reset link.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitForgot} className="space-y-3 mt-3">
              <Input data-testid="auth-forgot-email" type="email" required placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-none border-border focus-visible:ring-oxford" />
              <Button type="submit" disabled={loading} data-testid="auth-forgot-submit"
                className="w-full rounded-none bg-oxford hover:bg-[#2C5282] text-white h-11 gap-2">
                {loading && <Loader2 size={16} className="animate-spin" />} Send reset link
              </Button>
            </form>
            <button onClick={() => setMode("login")} className="mt-4 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mx-auto block">
              <ArrowLeft size={13} /> Back to log in
            </button>
          </div>
        )}

        {isFormMode && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl tracking-tight">
                {mode === "register" ? "Create your account" : "Welcome back"}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {mode === "register" ? "Save your reports, track progress, and build your Academic Profile." : "Log in to access your history and profile."}
              </DialogDescription>
            </DialogHeader>

            <Button type="button" onClick={google} data-testid="google-signin-btn"
              className="w-full rounded-none bg-white text-foreground border border-border hover:bg-secondary gap-2 h-11">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-1">
              <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <Input data-testid="auth-name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
                  className="rounded-none border-border focus-visible:ring-oxford" />
              )}
              <Input data-testid="auth-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-border focus-visible:ring-oxford" />
              <Input data-testid="auth-password" type="password" required placeholder={mode === "register" ? "Create a password (min 6 chars)" : "Password"}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none border-border focus-visible:ring-oxford" />
              {mode === "login" && (
                <button type="button" onClick={() => setMode("forgot")} data-testid="auth-forgot-link"
                  className="text-xs text-muted-foreground hover:text-oxford">Forgot your password?</button>
              )}
              {mode === "register" && (
                <div className="space-y-2 pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="auth-terms-wrapper">
                    <input
                      type="checkbox"
                      required
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      data-testid="auth-accept-terms"
                      className="mt-0.5 h-4 w-4 rounded-none border-border text-oxford focus:ring-oxford accent-[#1B365D]"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-oxford underline hover:no-underline">Terms of Service</a>
                      {" "}and{" "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="text-oxford underline hover:no-underline">Privacy Policy</a>.
                      <span className="text-red-600"> *</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="auth-marketing-wrapper">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      data-testid="auth-marketing-consent"
                      className="mt-0.5 h-4 w-4 rounded-none border-border text-oxford focus:ring-oxford accent-[#1B365D]"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I&apos;d like to receive MyAE news, product updates, tips and offers by email. Optional — you can unsubscribe any time.
                    </span>
                  </label>
                </div>
              )}
              <Button type="submit" disabled={loading || (mode === "register" && !acceptedTerms)} data-testid="auth-submit"
                className="w-full rounded-none bg-oxford hover:bg-[#2C5282] text-white h-11 gap-2">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {mode === "register" ? "Sign up" : "Log in"}
              </Button>
            </form>

            <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="auth-toggle-mode"
              className="text-sm text-oxford hover:underline mx-auto">
              {mode === "register" ? "Already have an account? Log in" : "Don't have an account? Sign up"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
